/**
 * Days whose availability score was withdrawn, and the one rule about them.
 *
 * A stretch of check-ins were never delivered: the presence watcher stopped
 * polling whenever its tab lost focus, so pings fell due and expired without
 * the prompt rendering. Availability is productive over expected and a miss
 * sits in the denominator, so those days scored people on where their browser
 * window was.
 *
 * Those days were withdrawn rather than recomputed. Nothing recorded what the
 * people were actually doing, so a fresh figure would be a guess wearing a
 * percentage sign; withdrawing says the smaller, true thing.
 *
 * The rule, and it is the whole reason this file exists rather than an inline
 * check at each of the six render sites: **a withdrawn day shows no
 * percentage**. Not greyed, not asterisked, not in brackets. A number rendered
 * beside a withdrawal is the same claim being made again with a caveat, and a
 * caveat is not a withdrawal. `averageAvailability` exists for the same
 * reason: an average is a place where a withdrawn figure gets asserted again
 * without anybody choosing to.
 */

export interface Withdrawable {
  scoringWithdrawnAt?: string | null;
  scoringWithdrawnReason?: string | null;
  withdrawnAvailabilityPct?: string | null;
}

export function isWithdrawn(day: Withdrawable | null | undefined): boolean {
  return Boolean(day?.scoringWithdrawnAt);
}

/**
 * Mean availability across days, skipping the withdrawn ones.
 *
 * Returns null when nothing is left to average — which is the right answer,
 * and distinct from 0%. A month where every day was withdrawn has no
 * availability figure, and showing 0 there would be the worst version of this
 * bug rather than a fix for it.
 */
export function averageAvailability(
  days: (Withdrawable & { availabilityPct?: string | number | null })[],
): number | null {
  const scored = days
    .filter((d) => !isWithdrawn(d))
    // Null and undefined are dropped BEFORE Number(), not after. Number(null)
    // is 0 and 0 is finite, so a day carrying no figure would average in as a
    // zero -- turning "nobody scored this day" into "they scored nothing",
    // which is the confusion this whole file exists to prevent.
    .filter((d) => d.availabilityPct !== null && d.availabilityPct !== undefined)
    .map((d) => Number(d.availabilityPct))
    .filter((n) => Number.isFinite(n));
  if (!scored.length) return null;
  return scored.reduce((sum, n) => sum + n, 0) / scored.length;
}

/** The sentence shown in place of the number, kept identical everywhere. */
export const WITHDRAWN_LABEL = 'Not scored';

export const WITHDRAWN_HINT =
  'Check-ins were not reaching this person’s browser on this day, so there is ' +
  'nothing to score. The figure this day used to show has not been replaced ' +
  'with a new one, because there is no honest way to work out what it should ' +
  'have been.';
